import type { NodeViewProps } from '@tiptap/core';
import { DRAFT_SECTION_SENTINEL } from './draft-sentinel';

export interface SectionDraftState {
  /** The section title attribute, or null. */
  sectionTitle: string | null;
  /** Raw heading text (sentinel not stripped) — callers strip it when linking. */
  headingRawText: string;
  /** The heading has no meaningful text (sentinel ignored). */
  headingEmpty: boolean;
  /** The section is a lone empty paragraph after the heading (sentinel ignored). */
  bodyEmpty: boolean;
  /** The section should render as a draft (placeholder + blur-to-delete lifecycle). */
  isDraftSection: boolean;
  /** The heading is locked because the section is linked to an EHR section. */
  isHeadingLocked: boolean;
  /** The linked EHR section id, or null. */
  linkedEhrSectionId: string | null;
}

/**
 * Derives the draft / empty / locked state of a section node from its content.
 * Pure and schema-only, so the vMarkdown and markdown-native section node views
 * share it rather than each carrying the derivation.
 *
 * A section is treated as a draft when any of these hold:
 *  1. the `isDraftSection` attribute is set (the original mechanism), or
 *  2. the heading contains only the sentinel (survives text-preserving syncs), or
 *  3. it has the default "New Section" title with genuinely empty content
 *     (fallback for backends that normalise away zero-width characters).
 */
export function getSectionDraftState(node: NodeViewProps['node']): SectionDraftState {
  const sectionTitle = (node.attrs.sectionTitle as string | null) ?? null;

  const contentBlocks = node.content?.content ?? [];
  const headingNode = contentBlocks[0];
  const bodyNode = contentBlocks[1];

  const headingRawText = headingNode?.textContent ?? '';
  const bodyRawText = bodyNode?.textContent ?? '';

  const headingEmpty = Boolean(
    headingNode?.type.name === 'heading' &&
    !headingRawText.replace(DRAFT_SECTION_SENTINEL, '').trim()
  );
  // Only show the body placeholder when the section has exactly one paragraph
  // (heading + 1 paragraph). Additional content nodes mean it has real content.
  const bodyEmpty = Boolean(
    bodyNode?.type.name === 'paragraph' &&
    contentBlocks.length === 2 &&
    !bodyRawText.replace(DRAFT_SECTION_SENTINEL, '').trim()
  );

  const headingHasSentinelOnly =
    headingRawText.includes(DRAFT_SECTION_SENTINEL) &&
    !headingRawText.replace(DRAFT_SECTION_SENTINEL, '').trim();
  const hasSentinelOnlyDraftContent = headingHasSentinelOnly && bodyEmpty;

  const isDraftSection =
    node.attrs.isDraftSection === true ||
    hasSentinelOnlyDraftContent ||
    (sectionTitle === 'New Section' && headingEmpty && bodyEmpty);

  const linkedEhrSectionId = (node.attrs.linkedEhrSectionId as string) ?? null;

  return {
    sectionTitle,
    headingRawText,
    headingEmpty,
    bodyEmpty,
    isDraftSection,
    isHeadingLocked: Boolean(linkedEhrSectionId),
    linkedEhrSectionId,
  };
}
